// Searchable select (combobox): a Select-styled trigger opening a filterable
// option list — for pickers whose lists outgrow a native <select> (assets,
// users, sub-types). Compose with <Field> for label/hint like Select.
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { CloseIcon } from '@xantory/icons';
import { useT } from '@xantory/i18n';
import { Dot, type DotStatus } from '../Dot/dot';
import { FieldLabelContext } from '../Field/field';
import { styles } from './search-select.styles';
export interface SearchSelectOption {
  value: string;
  label: string;
  /** Extra text the filter matches but the list never DISPLAYS.
   *
   *  For pickers where an operator types something that identifies the option
   *  without appearing in its label — a crop's variety, a device's serial. Without
   *  it the only way to make such a term findable is to append it to `label`,
   *  which then shows a wall of text in the trigger. Absent means label-only. */
  keywords?: string;
  /** A status dot after the label, in the LIST only (the trigger stays plain text) — e.g. a
   *  filter option whose items need attention. `label` is spoken in place of the colour, so the
   *  dot is never the only carrier of its meaning. Absent means no dot. */
  status?: {
    dot: DotStatus;
    label: string;
  };
}
interface SearchSelectProps {
  /** Names the control when there is no `placeholder` and no enclosing
   *  `<Field label>` — a toolbar filter, typically.
   *
   *  ⚠ It has to be DECLARED. This component does not spread rest props, so an
   *  `aria-label` passed by a caller was silently dropped and the trigger had no
   *  accessible name at all — which is what the native `<select>` these replaced
   *  had been providing (2026-09-16). */
  'aria-label'?: string;
  value: string;
  options: SearchSelectOption[];
  /** Trigger text while nothing is selected.
   *
   *  ⚠ NOT the search box's placeholder — it used to be both, which is how a
   *  field whose empty state reads "unset" ended up with a search box saying
   *  "unset" sitting above an "unset" option. Two controls, one word, and the
   *  operator cannot tell which one they are typing into. */
  placeholder?: string;
  /** Overrides the search box's own placeholder. Defaults to "Search here…" —
   *  pass this only when a field needs to say what it searches. */
  searchPlaceholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Marks the field invalid: danger border + danger focus ring + aria-invalid. */
  invalid?: boolean;
  /** The ✕ on the trigger that puts the field back to empty. **Defaults to
   *  true** — every dropdown in this console offers it (§27).
   *
   *  ⚠ It used to be `clearLabel`, which rendered "nothing" as the FIRST OPTION
   *  — so a field labelled "Asset class" showed "Asset class" sitting in its own
   *  option list, reading as a choosable value. The list now holds options and
   *  nothing else; clearing is an affordance on the CONTROL, where every other
   *  clearable input puts it.
   *
   *  ⚠⚠ Default ON, not opt-in. Opt-in meant only the 45 fields that happened to
   *  carry the old `clearLabel` got an ✕, and the other 134 silently trapped the
   *  operator on their first choice (user, 2026-09-16). Pass `clearable={false}`
   *  only where an empty value is genuinely unrepresentable. */
  clearable?: boolean;
  /** @deprecated Unused — kept only so the ~45 call sites this repo has not
   *  yet swept still typecheck against the new prop shape. `clearable`
   *  (default true) already gives every one of them a working clear
   *  affordance; this is accepted and ignored, never rendered as a fake
   *  option the way it used to be. Safe to delete at each call site
   *  whenever that page is next touched. */
  clearLabel?: string;
  /** Goes on the TRIGGER, so a `<Field htmlFor>` label points at something real.
   *  Without it a Field's `<label htmlFor>` dangles and clicking the label does
   *  nothing — the same association a native <select id> gave for free. */
  id?: string;
}
const MAX_VISIBLE = 100;
/** The panel's floor width — a readable panel even off a very small control. */
const PANEL_MIN_WIDTH = 220;
/** Trigger → panel gap, and the margin the panel keeps from every viewport edge. */
const PANEL_GAP = 4;
const EDGE = 16;
/** A list squeezed below this shows too little to choose from; the panel then
 *  overlaps the edge margin rather than shrink further. */
const MIN_LIST_HEIGHT = 120;

/** Case-folded with runs of the same character collapsed.
 *
 *  So a doubled letter neither has to be typed nor guessed: "lolo" and "lollo"
 *  both fold to "lolo", and either finds "Lollo Rossa". Operators type cultivar
 *  names from memory and a strict substring match answers a one-letter slip with
 *  an empty list, which reads as "we do not grow that". */
const fold = (s: string) => s.toLowerCase().replace(/(.)\1+/g, '$1');
export function SearchSelect({
  value,
  options,
  placeholder,
  searchPlaceholder,
  onChange,
  disabled = false,
  invalid = false,
  clearable = true,
  id,
  'aria-label': ariaLabel
}: SearchSelectProps) {
  const t = useT('ui');
  const fieldLabel = useContext(FieldLabelContext);

  /**
   * §27 — a form dropdown's empty state reads "Select Asset class", not
   * "Asset class".
   *
   * ⚠ Derived, not passed. 101 call sites would otherwise each have to repeat
   * their field's label in a second string, and the ones that got it wrong are
   * exactly what the user reported: a trigger reading "Asset class" is
   * indistinguishable from a chosen value.
   *
   * Two cases, and only these two:
   *  - no `placeholder` inside a `Field` → "Select {label}".
   *  - a `placeholder` that IS the field's label → "Select {label}", because
   *    repeating the label was the bug.
   * Anything else is left alone: a FILTER says "All classes" / "Domain: all",
   * whose empty state means "everything", not "nothing chosen yet".
   */
  const shownPlaceholder = fieldLabel && (placeholder === undefined || placeholder === fieldLabel) ? t('selectLabel', {
    label: fieldLabel
  }) : placeholder ?? '';

  /** What assistive tech calls this control: an explicit `aria-label` first,
   *  then whatever the trigger displays when nothing is chosen. */
  const accessibleName = ariaLabel ?? shownPlaceholder;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /**
   * ⚠⚠ The panel FLOATS: `position: fixed`, placed from the trigger's own rect on
   * every open, scroll and resize (pitfalls #8). It used to be `absolute` inside
   * whatever scrolled around it, and a Table is exactly such a box: the request
   * line editor's class picker opened INSIDE the table's scroll envelope, which
   * scrolled its own rows out of sight and left the last options unreachable
   * (measured 2026-09-29: 8 classes, 3 behind the table's bottom edge).
   *
   * What `place` guarantees, all measured rather than assumed:
   *  - never off-screen: flips LEFTWARD when there is no room to the right (the
   *    2026-09-17 "dropdowns going outside the screen" report), clamped to a
   *    16px edge either way;
   *  - opens UPWARD when the room below is short and there is more above;
   *  - the option list shrinks to the room it has, so the last option is always
   *    reachable by scrolling the list rather than the page.
   *
   * ⚠ No portal, deliberately. A Modal/Drawer traps focus inside itself, so a
   * panel moved to <body> would lose its own search box to the trap. Those
   * containers use `transform`, which makes THEM the fixed box instead of the
   * viewport — so `place` measures that box's origin and subtracts it, and the
   * same math holds in both cases. Their scrolling BODY sits inside that box, so
   * it no longer clips the panel either.
   */
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const r = trigger.getBoundingClientRect();
    const list = listRef.current;
    if (list) list.style.maxHeight = '';
    panel.style.minWidth = `${Math.max(r.width, PANEL_MIN_WIDTH)}px`;
    // At (0,0) the panel's rect IS the origin of its fixed box, and its natural size.
    panel.style.top = '0px';
    panel.style.left = '0px';
    const o = panel.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - PANEL_GAP - EDGE;
    const above = r.top - PANEL_GAP - EDGE;
    const up = o.height > below && above > below;
    const room = Math.max(up ? above : below, 0);
    if (list && o.height > room) {
      list.style.maxHeight = `${Math.max(list.clientHeight - (o.height - room), MIN_LIST_HEIGHT)}px`;
    }
    const h = panel.getBoundingClientRect().height;
    const alignEnd = r.left + o.width > window.innerWidth - EDGE;
    const left = Math.max(EDGE, Math.min(alignEnd ? r.right - o.width : r.left, window.innerWidth - EDGE - o.width));
    const top = up ? r.top - PANEL_GAP - h : r.bottom + PANEL_GAP;
    panel.style.top = `${top - o.top}px`;
    panel.style.left = `${left - o.left}px`;
  }, []);

  // Before paint, so the panel is never seen at its (0,0) measuring spot. Re-run
  // when the options change: filtering shortens the list and moves an upward panel.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place, q, options]);
  useEffect(() => {
    if (!open) return;
    // Follow the trigger rather than closing: opening can itself scroll the page
    // (focusing the search box, a phone keyboard resizing the viewport), and a
    // panel that closed on that would never stay open. The list's OWN scroll is
    // ignored — it moves nothing the panel is anchored to.
    let frame = 0;
    const onMove = (e: Event) => {
      if (e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Layered Esc: only the panel closes — capture + stopPropagation keeps
        // the event from a containing Modal's own Esc-to-close listener.
        e.stopPropagation();
        setOpen(false);
        // Keyboard close must not drop focus on <body> (WCAG 2.4.3).
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    // `preventScroll`: the panel is already placed; focusing must not scroll a
    // container under it and move the trigger away from it.
    inputRef.current?.focus({
      preventScroll: true
    });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);
  const selected = options.find(o => o.value === value);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    // Label and keywords are tested SEPARATELY rather than concatenated: joining
    // them lets a needle match across the boundary between two unrelated fields.
    // Each is tried verbatim first, then folded, so an exact hit always wins and
    // the tolerance only ever ADDS matches.
    const soft = fold(needle);
    return options.filter(o => {
      const label = o.label.toLowerCase();
      const kw = o.keywords?.toLowerCase() ?? '';
      return label.includes(needle) || kw.includes(needle) || fold(label).includes(soft) || fold(kw).includes(soft);
    });
  }, [options, q]);
  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQ('');
    triggerRef.current?.focus();
    // Picking is a real edit, but option clicks emit no input/change event —
    // surface one so container-level dirty tracking (Modal's discard guard) sees it.
    rootRef.current?.dispatchEvent(new Event('change', {
      bubbles: true
    }));
  };
  const optionEls = () => Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);

  // Roving focus: ArrowDown from the search input enters the list; arrows walk
  // it; ArrowUp off the top returns to the input. Enter activates natively.
  const onSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && filtered.length > 0) {
      pick(filtered[0]!.value);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      optionEls()[0]?.focus();
    }
  };
  const onListKeyDown = (e: KeyboardEvent) => {
    const els = optionEls();
    const i = els.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      els[Math.min(i + 1, els.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (i <= 0) inputRef.current?.focus();else els[i - 1]?.focus();
    }
  };
  return <div ref={rootRef} {...stylex.props(styles.root)}>
      <button ref={triggerRef} id={id} type="button" disabled={disabled} {...stylex.props(styles.trigger, !clearable && styles.triggerNoClear, disabled && styles.triggerDisabled, invalid && styles.triggerInvalid)} aria-haspopup="listbox" aria-expanded={open} aria-invalid={invalid || undefined}
    // Only when given: otherwise the trigger's own text (the selection, or
    // the placeholder) is the accessible name, which is what it should be.
    aria-label={ariaLabel} onClick={() => setOpen(v => !v)}>
        <span {...stylex.props(styles.triggerLabel, !selected && styles.placeholder)}>
          {selected?.label ?? shownPlaceholder}
        </span>
      </button>

      {/* ⚠ A SIBLING of the trigger, not a child: a button inside a button is
          `nested-interactive` and a screen reader cannot reach the inner one.
          It sits over the trigger's right edge, before the chevron. */}
      {clearable && value !== '' && !disabled && <button type="button" aria-label={t('clearSelection')} title={t('clearSelection')} onClick={e => {
      e.stopPropagation();
      onChange('');
      setOpen(false);
    }} {...stylex.props(styles.clearBtn)}>
          <CloseIcon size={13} />
        </button>}
      {open && <div ref={panelRef} {...stylex.props(styles.panel)}>
          <input ref={inputRef} data-dirty-ignore {...stylex.props(styles.search)} aria-label={t('searchOptions')} placeholder={searchPlaceholder ?? t('searchHere')} value={q} onInput={e => setQ((e.currentTarget as HTMLInputElement).value)} onKeyDown={onSearchKeyDown} />
          {/* listbox = ONLY option children, and only when options exist —
              an input inside it / an empty listbox both violate ARIA. */}
          {filtered.length > 0 && <div ref={listRef} role="listbox" aria-label={accessibleName} {...stylex.props(styles.list)} onKeyDown={onListKeyDown}>
              {filtered.slice(0, MAX_VISIBLE).map(o => <button type="button" key={o.value} role="option" aria-selected={o.value === value} {...stylex.props(styles.option, o.value === value && styles.optionSelected)} onClick={() => pick(o.value)}>
                  {o.label}
                  {o.status && <>
                      <span aria-hidden="true" {...stylex.props(styles.optionDot)}>
                        <Dot status={o.status.dot} />
                      </span>
                      <span {...stylex.props(styles.srOnly)}>, {o.status.label}</span>
                    </>}
                </button>)}
            </div>}
          {filtered.length === 0 && <div {...stylex.props(styles.emptyNote)}>—</div>}
          {filtered.length > MAX_VISIBLE && <div {...stylex.props(styles.emptyNote)}>+{filtered.length - MAX_VISIBLE}…</div>}
        </div>}
    </div>;
}